import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  AutocompleteOption,
  GroupedAutocompleteOptions,
  ServerSideDataSource,
} from '@eagle/shared';

import { VacancyAllData, ListVacancyModel } from '../models';
import { VacanciesDataService } from './vacancies-data.service';

export class VacanciesDataSourceService extends ServerSideDataSource<ListVacancyModel> {
  protected loadingSubject = new BehaviorSubject<boolean>(false);
  protected itemsSubject = new BehaviorSubject<ListVacancyModel[]>([]);

  private autocompleteSearch$ = new Subject<{
    search: string;
    existingFilters: AutocompleteOption[];
  }>();
  readonly autocompleteOptions$: Observable<GroupedAutocompleteOptions[]> = this.autocompleteSearch$
    .asObservable()
    .pipe(
      switchMap(({ search, existingFilters }) =>
        this.vacanciesDataService.getSuggestionsList(search).pipe(
          this.recoverFromError(),
          map(groupedOptions =>
            groupedOptions
              .map(group => {
                group.categories = group.categories.filter(
                  o => !existingFilters.find(f => f.category === o.category && f.value === o.value)
                );
                return group;
              })
              .filter(group => group.categories.length)
          )
        )
      )
    );
  private _filters$ = new Subject();

  selection = new SelectionModel<ListVacancyModel>(true, []);
  loading$ = this.loadingSubject.asObservable();
  data: ListVacancyModel[] = [];
  totalElements = 0;
  isOnInexistentPage: boolean;

  constructor(
    protected vacanciesDataService: VacanciesDataService,
    public sort: MatSort,
    public paginator: MatPaginator
  ) {
    super(sort, paginator);

    this._filters$
      .pipe(
        switchMap(params =>
          this.vacanciesDataService.getListData(params).pipe(this.recoverFromError())
        ),
        tap(({ vacancies: { totalElements, number, totalPages } }: VacancyAllData) => {
          this.totalElements = totalElements;
          this.isOnInexistentPage = number ? number + 1 > totalPages : false;
        }),
        map((entitiesAllData: VacancyAllData) => entitiesAllData.vacancies.content),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((entities: ListVacancyModel[]) => {
        this.setData(entities);
      });
  }

  getAll(): void {
    this.selection.clear();
    this.loadingSubject.next(true);

    const params = {
      sortBy: `${this.sort.active || 'created_at'}:${this.sort.direction || 'desc'}`,
      size: this.paginator.pageSize || this.PAGE_SIZE,
      page: this.paginator.pageIndex || 0,
      ...this.filters,
    };

    this._filters$.next(params);
  }

  loadAutocompleteOptions(search: string, existingFilters: AutocompleteOption[] = []): void {
    this.autocompleteSearch$.next({ search, existingFilters });
  }
}
